import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { adminLogin, listBookings, updateBookingStatus } from "@/lib/api";
import { translations } from "@/i18n/content";

const STATUS_COLORS = {
  pending: "bg-sand/30 text-navy border-sand",
  confirmed: "bg-ocean/15 text-ocean border-ocean",
  cancelled: "bg-red-100 text-red-700 border-red-300",
};

export default function Admin() {
  const [passcode, setPasscode] = useState(sessionStorage.getItem("azt_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");

  const load = async (pc) => {
    setLoading(true);
    try {
      const data = await listBookings(pc);
      setBookings(data);
      setAuthed(true);
      setPasscode(pc);
      sessionStorage.setItem("azt_admin", pc);
    } catch {
      toast.error("Invalid passcode");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (passcode) load(passcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(input);
      load(input);
    } catch {
      toast.error("Invalid passcode");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("azt_admin");
    setAuthed(false);
    setPasscode("");
    setInput("");
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateBookingStatus(id, status, passcode);
      setBookings((bs) => bs.map((b) => (b.id === id ? updated : b)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const en = translations.en;

  if (!authed) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-5">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="bg-cream border border-black/10 p-8 md:p-10 w-full max-w-md"
        >
          <p className="label-mono text-ocean mb-3">Admin</p>
          <h1 className="font-playfair text-3xl text-ink mb-6">Bookings Dashboard</h1>
          <Input
            data-testid="admin-passcode"
            type="password"
            placeholder="Passcode"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="rounded-none border-black/15 h-12 focus-visible:ring-1 focus-visible:ring-navy"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="mt-4 w-full bg-navy text-white label-mono py-4 hover:bg-ocean transition-colors"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 flex items-center justify-between">
          <div>
            <p className="label-mono text-sand">Atlas Ziz Tours</p>
            <h1 className="font-playfair text-2xl md:text-3xl">Bookings Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => load(passcode)}
              className="border border-white/25 p-3 hover:bg-white/10 transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="border border-white/25 label-mono px-4 py-3 flex items-center gap-2 hover:bg-white/10 transition-colors"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {["pending", "confirmed", "cancelled"].map((s) => (
            <div key={s} className="border border-black/10 bg-surface p-5">
              <p className="label-mono text-ink/50">{s}</p>
              <p className="font-outfit font-semibold text-3xl text-ink mt-1">
                {bookings.filter((b) => b.status === s).length}
              </p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-ocean" size={32} />
          </div>
        ) : bookings.length === 0 ? (
          <p className="font-manrope text-ink/50 py-20 text-center">No bookings yet.</p>
        ) : (
          <div className="border border-black/10 overflow-x-auto">
            <table className="w-full text-left" data-testid="admin-bookings-table">
              <thead className="bg-surface border-b border-black/10">
                <tr className="label-mono text-ink/60">
                  <th className="p-4">Guest</th>
                  <th className="p-4">Lesson</th>
                  <th className="p-4">Date / Slot</th>
                  <th className="p-4">People</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`admin-booking-${b.id}`} className="border-b border-black/5 hover:bg-surface/50">
                    <td className="p-4 font-manrope">
                      <div className="font-semibold text-ink">{b.name}</div>
                      <div className="text-xs text-ink/50 font-mono uppercase">{b.language}</div>
                    </td>
                    <td className="p-4 font-manrope text-ink/80">{en.lessons.names[b.lesson_type] || b.lesson_type}</td>
                    <td className="p-4 font-mono text-sm text-ink/70">
                      {b.date}
                      <div className="text-xs text-ink/50">{en.schedule.slotNames[b.time_slot] || b.time_slot}</div>
                    </td>
                    <td className="p-4 font-mono">{b.people}</td>
                    <td className="p-4 font-manrope text-sm">
                      <div className="text-ink/80">{b.email}</div>
                      <div className="text-ink/50">{b.phone}</div>
                    </td>
                    <td className="p-4">
                      <span className={`label-mono border px-2 py-1 ${STATUS_COLORS[b.status]}`}>
                        {b.status}
                      </span>
                      <div className="flex gap-2 mt-2">
                        {b.status !== "confirmed" && (
                          <button
                            data-testid={`confirm-${b.id}`}
                            onClick={() => changeStatus(b.id, "confirmed")}
                            className="text-xs font-mono text-ocean hover:underline"
                          >
                            Confirm
                          </button>
                        )}
                        {b.status !== "cancelled" && (
                          <button
                            data-testid={`cancel-${b.id}`}
                            onClick={() => changeStatus(b.id, "cancelled")}
                            className="text-xs font-mono text-red-600 hover:underline"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
